import { useCallback, useMemo } from 'react';
import { SearchParamsValue } from '@apps/stays/components/SearchParamsObserver/SearchParamsObserver';
import { DEFAULT_LOCATION } from './constants';
import { useGeocoding } from '@shared/hooks/useGeocoding';
import { useEventInfo } from '@shared/utils/eventInfo';
import { AccommodationsConsoleStateInitializationQuery } from '@graphql/generated';

type BestLocationAnchor = NonNullable<AccommodationsConsoleStateInitializationQuery['eventByName']>['bestLocationAnchor'];

export const useDeriveLocationQueryParams = (bestLocationAnchor: BestLocationAnchor | null | undefined, isLoading: boolean, isCalled: boolean) => {
  const { eventInfo } = useEventInfo();
  const locationName = useMemo(() => eventInfo?.locationInfo?.name || '', [eventInfo?.locationInfo?.name]);

  // Only geocode if we don't have a location anchor and have a name to geocode
  const shouldGeocode = useMemo(() => Boolean(!bestLocationAnchor && locationName && !isLoading), [bestLocationAnchor, locationName, isLoading]);

  // Use geocoding with skip to prevent unnecessary API calls
  const { result: geocodingResult, isCalled: isGeoCalled } = useGeocoding({
    address: locationName,
    skip: !shouldGeocode,
  });

  // Memoize the derived location data to prevent unnecessary recalculations
  const derivedLocation = useMemo(() => {
    if (bestLocationAnchor) {
      return {
        lat: parseFloat(bestLocationAnchor.latitude),
        lng: parseFloat(bestLocationAnchor.longitude),
        destination: bestLocationAnchor.friendlyName,
      };
    }

    if (geocodingResult && !isLoading && !bestLocationAnchor) {
      return {
        lat: geocodingResult.location.lat,
        lng: geocodingResult.location.lng,
        destination: locationName,
      };
    }

    return null;
  }, [bestLocationAnchor, geocodingResult, isLoading, locationName]);

  return useCallback(
    ({ locationSearchParams, useDefaults }: { locationSearchParams: Pick<SearchParamsValue, 'destination' | 'lat' | 'lng'>; useDefaults: boolean }) => {
      // 1. Use the search params if they're valid
      if (!useDefaults && locationSearchParams.lat.valid && locationSearchParams.lng.valid) {
        return {
          lat: locationSearchParams.lat.value,
          lng: locationSearchParams.lng.value,
          destination: locationSearchParams.destination.valid ? locationSearchParams.destination.value : undefined,
          isMissingInUrl: false,
        };
      }

      // 2. Use derived location if available
      if (derivedLocation) {
        return {
          ...derivedLocation,
          isMissingInUrl: true,
        };
      }

      const shouldDefaultLocation = isCalled && isGeoCalled && !derivedLocation && !isLoading;
      // 3. Use default location only when not loading
      if (shouldDefaultLocation) {
        return {
          ...DEFAULT_LOCATION,
          isMissingInUrl: true,
        };
      }

      // 3. Return null while loading
      return null;
    },
    [derivedLocation, isCalled, isGeoCalled, isLoading],
  );
};
